import { supabase } from '../lib/supabase';
import { api } from './api';
import { API_BASE_URL } from '../utils/apiUrl';

export interface SaveMockupData {
  name: string;
  templateId: string;
  templateName: string;
  mockupFile: Blob;
  thumbnailFile?: Blob;
  settings: any;
  width?: number;
  height?: number;
}

export interface Mockup {
  id: string;
  user_id: string;
  name: string;
  template_name: string;
  mockup_url: string;
  thumbnail_url?: string;
  is_favorite: boolean;
  downloads: number;
  file_size?: number;
  width?: number;
  height?: number;
  settings: any;
  created_at: string;
  updated_at: string;
}

class LibraryService {
  async saveMockup(data: SaveMockupData): Promise<{ mockup?: Mockup; error?: string }> {
    try {
      console.log('Saving mockup to media backend');
      console.log('Mockup file:', data.mockupFile.type, data.mockupFile.size);

      const formData = new FormData();
      formData.append('name', data.name);
      formData.append('templateId', data.templateId);
      formData.append('templateName', data.templateName);
      formData.append('mockupFile', data.mockupFile, `${data.templateId || 'mockup'}.png`);
      formData.append('settings', JSON.stringify(data.settings || {}));

      if (data.thumbnailFile) {
        formData.append('thumbnailFile', data.thumbnailFile, `thumbnail-${data.templateId || 'mockup'}.png`);
      }
      if (typeof data.width === 'number') {
        formData.append('width', String(data.width));
      }
      if (typeof data.height === 'number') {
        formData.append('height', String(data.height));
      }

      const response = await api.post<{ success: boolean; mockup: Mockup }>('/api/media/mockups', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const mockup = response.data.mockup;

      console.log('Mockup saved successfully:', mockup);
      return { mockup };
    } catch (error: any) {
      console.error('Error saving mockup:', error);
      return { error: error.message || 'Failed to save mockup' };
    }
  }

  async getUserMockups(options?: { 
    limit?: number; 
    offset?: number; 
    favoritesOnly?: boolean;
    searchQuery?: string;
  }): Promise<{ mockups: Mockup[]; count: number; error?: string }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      let query = supabase
        .from('mockups')
        .select('*', { count: 'exact' })
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (options?.favoritesOnly) {
        query = query.eq('is_favorite', true);
      }

      if (options?.searchQuery) {
        query = query.or(`name.ilike.%${options.searchQuery}%,template_name.ilike.%${options.searchQuery}%`);
      }

      if (options?.limit) {
        query = query.limit(options.limit);
      }

      if (options?.offset) {
        query = query.range(options.offset, options.offset + (options.limit || 10) - 1);
      }

      const { data: mockups, count, error } = await query;

      if (error) throw error;

      return { mockups: mockups || [], count: count || 0 };
    } catch (error: any) {
      console.error('Error fetching mockups:', error);
      return { mockups: [], count: 0, error: error.message };
    }
  }

  async toggleFavorite(mockupId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // First get current state
      const { data: mockup } = await supabase
        .from('mockups')
        .select('is_favorite')
        .eq('id', mockupId)
        .eq('user_id', user.id)
        .single();

      if (!mockup) throw new Error('Mockup not found');

      // Toggle favorite
      const { error } = await supabase
        .from('mockups')
        .update({ is_favorite: !mockup.is_favorite })
        .eq('id', mockupId)
        .eq('user_id', user.id);

      if (error) throw error;

      return { success: true };
    } catch (error: any) {
      console.error('Error toggling favorite:', error);
      return { success: false, error: error.message };
    }
  }

  async deleteMockup(mockupId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      await api.delete(`/api/media/mockups/${mockupId}`);

      console.log('Mockup deleted successfully');
      return { success: true };
    } catch (error: any) {
      console.error('Error deleting mockup:', error);
      return { success: false, error: error.message };
    }
  }

  async incrementDownloadCount(mockupId: string): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase.rpc('increment', { 
        table_name: 'mockups',
        row_id: mockupId,
        column_name: 'downloads'
      });
    } catch (error) {
      console.error('Error incrementing download count:', error);
    }
  }

  async downloadMockup(mockup: Mockup): Promise<{ success: boolean; error?: string }> {
    try {
      // Route download through backend to avoid CORS issues with GCS URLs
      const response = await api.get(`/api/download/${mockup.id}/png`, {
        params: { sourceUrl: mockup.mockup_url },
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'image/png' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${mockup.name.replace(/\s+/g, '-')}-${mockup.id.slice(0, 8)}.png`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      console.log('Mockup downloaded:', mockup.name);
      return { success: true };
    } catch (error: any) {
      console.error('Error downloading mockup:', error);
      return { success: false, error: error.message };
    }
  }

  async removeBackground(mockupId: string, mockupUrl: string): Promise<{ mockup?: { id: string; url: string; filename: string }; error?: string }> {
    try {
      console.log('Removing background from library mockup:', mockupId);
      
      // Call the backend API to remove background
      const response = await api.post<{ 
        success: boolean; 
        mockup: { id: string; filename: string; url: string; generatedAt: Date }; 
        error?: string 
      }>('/api/mockup/remove-background/library', { 
        mockupId,
        mockupUrl 
      });
      
      if (response.data.success && response.data.mockup) {
        return { 
          mockup: {
            id: response.data.mockup.id,
            url: response.data.mockup.url,
            filename: response.data.mockup.filename
          }
        };
      }
      
      throw new Error(response.data.error || 'Failed to remove background');
    } catch (error: any) {
      console.error('Error removing background:', error);
      return { error: error.message || 'Failed to remove background' };
    }
  }
}

export const libraryService = new LibraryService();
